/**
 * Live Monitor Tab - Real-time presence monitoring
 */

import { useState, useMemo } from 'react';
import { IconSearch, IconEye } from '@tabler/icons-react';
import { Text, Stack, Group, Select, TextInput, Badge, ActionIcon, Avatar } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { IdleTimeDisplay } from '../../../../components/ui/IdleTimeDisplay';
import { getStatusColor } from '../../../../utils/presenceHelpers';
import { usePresenceManager } from '../../../../hooks/usePresenceManager';
import type { UserPresenceUpdate } from '@actschurch/ccms-sdk';

interface LiveMonitorTabProps {
  onViewUser?: (user: UserPresenceUpdate) => void;
}

export function LiveMonitorTab({ onViewUser }: LiveMonitorTabProps) {
  const { onlineUsers, isConnected } = usePresenceManager();
  const [liveUsersFilter, setLiveUsersFilter] = useState<string>('all');
  const [liveUsersSearch, setLiveUsersSearch] = useState('');

  const filteredLiveUsers = useMemo(() => {
    let filtered = onlineUsers;

    // Filter by status
    if (liveUsersFilter !== 'all') {
      filtered = filtered.filter((u) => u.status === liveUsersFilter);
    }

    // Search filter
    if (liveUsersSearch.trim()) {
      const query = liveUsersSearch.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.userId?.toLowerCase().includes(query) ||
          u.userName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [onlineUsers, liveUsersFilter, liveUsersSearch]);

  const columns: DataTableColumn<UserPresenceUpdate & { id: string }>[] = [
    {
      key: 'userId',
      label: 'User',
      sortable: true,
      render: (_value, row) => (
        <Group gap="xs">
          <Avatar size="sm" radius="xl">
            {row.userName?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Stack gap={0}>
            <Text size="sm" fw={500}>
              {row.userId}
            </Text>
            {row.userName && (
              <Text size="xs" c="dimmed">
                {row.userName}
              </Text>
            )}
          </Stack>
        </Group>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_value, row) => (
        <Badge color={getStatusColor(row.status)}>{row.status}</Badge>
      ),
    },
    {
      key: 'platform',
      label: 'Platform',
      render: (_value, row) => <Badge variant="outline">{row.platform || 'N/A'}</Badge>,
    },
    {
      key: 'activityMessage',
      label: 'Activity',
      render: (_value, row) => row.activityMessage || '-',
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      sortable: true,
      render: (_value, row) => <IdleTimeDisplay lastSeen={row.lastSeen} size="xs" />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, row) => (
        <ActionIcon variant="light" onClick={() => onViewUser?.(row)}>
          <IconEye size={16} />
        </ActionIcon>
      ),
    },
  ];

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Real-time presence monitoring ({filteredLiveUsers.length} users)
        </Text>
        <Badge color={isConnected ? 'green' : 'red'} variant="filled">
          {isConnected ? 'Connected' : 'Disconnected'}
        </Badge>
      </Group>

      {/* Filters */}
      <Group>
        <TextInput
          placeholder="Search users..."
          leftSection={<IconSearch size={16} />}
          value={liveUsersSearch}
          onChange={(e) => setLiveUsersSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          placeholder="Filter by status"
          value={liveUsersFilter}
          onChange={(value) => setLiveUsersFilter(value || 'all')}
          data={[
            { value: 'all', label: 'All Statuses' },
            { value: 'Online', label: 'Online' },
            { value: 'Away', label: 'Away' },
            { value: 'Busy', label: 'Busy' },
          ]}
          style={{ width: 180 }}
        />
      </Group>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredLiveUsers.map((u) => ({ ...u, id: u.userId }))}
        enableInternalSearch={false}
        emptyMessage="No users online"
      />
    </Stack>
  );
}
